import React from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

const SITE_NAME = "Victor Springer";
const SITE_URL = "https://victorspringer.com";
const DEFAULT_IMAGE = `${SITE_URL}/avatar.jpg`;
const DEFAULT_IMAGE_WIDTH = 512;
const DEFAULT_IMAGE_HEIGHT = 512;
const DEFAULT_LOCALE = "en_US";

const buildCanonicalUrl = (pathname) => {
  if (!pathname || pathname === "/") {
    return SITE_URL;
  }

  const trimmed = pathname.replace(/\/+$/, "") || "/";
  return `${SITE_URL}${trimmed === "/" ? "" : trimmed}`;
};

const Seo = ({
  title,
  description,
  type = "website",
  image = DEFAULT_IMAGE,
  imageWidth,
  imageHeight,
  imageAlt,
  noindex = false,
  structuredData,
  canonicalPath,
  author = SITE_NAME,
  locale = DEFAULT_LOCALE,
  keywords,
  publishedTime,
  modifiedTime,
  section,
  tags,
}) => {
  const location = useLocation();
  const pathname = canonicalPath || location.pathname;
  const canonicalUrl = buildCanonicalUrl(pathname);
  const robots = noindex
    ? "noindex, nofollow"
    : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";
  const fullTitle = `${title} | ${SITE_NAME}`;
  const isDefaultImage = image === DEFAULT_IMAGE;
  const resolvedImageWidth = imageWidth || (isDefaultImage ? DEFAULT_IMAGE_WIDTH : undefined);
  const resolvedImageHeight = imageHeight || (isDefaultImage ? DEFAULT_IMAGE_HEIGHT : undefined);
  const resolvedImageAlt = imageAlt || `${SITE_NAME} — ${title}`;
  const keywordList = Array.isArray(keywords) ? keywords.join(", ") : keywords;
  const articleTags = Array.isArray(tags) ? tags : tags ? [tags] : [];
  const structuredDataItems = Array.isArray(structuredData)
    ? structuredData
    : structuredData
      ? [structuredData]
      : [];

  return (
    <Helmet>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <meta name="author" content={author} />
      {keywordList && <meta name="keywords" content={keywordList} />}
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={locale} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:image:secure_url" content={image} />
      {resolvedImageWidth && (
        <meta property="og:image:width" content={String(resolvedImageWidth)} />
      )}
      {resolvedImageHeight && (
        <meta property="og:image:height" content={String(resolvedImageHeight)} />
      )}
      <meta property="og:image:alt" content={resolvedImageAlt} />

      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === "article" && (
        <meta property="article:author" content={author} />
      )}
      {type === "article" && section && (
        <meta property="article:section" content={section} />
      )}
      {type === "article" &&
        articleTags.map((tag) => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={resolvedImageAlt} />

      {structuredDataItems.map((item, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
